import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { useCustomerAuth } from "../context/CustomerAuthContext";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { completeGoogleLogin } = useCustomerAuth();
  const [error, setError] = useState("");
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const providerError = params.get("error");
    const code = params.get("code");
    const state = params.get("state");

    if (providerError) {
      setError("Google sign-in was cancelled or denied.");
      return;
    }
    if (!code) {
      setError("Google sign-in code is missing. Please try again.");
      return;
    }

    (async () => {
      try {
        const data = await completeGoogleLogin(code, state);
        const redirectAfter = data.redirect_after || "/profile";
        window.history.replaceState(null, "", redirectAfter);
        navigate(redirectAfter, { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail || err.message || "Google sign-in failed.";
        setError(typeof detail === "string" ? detail : "Google sign-in failed.");
      }
    })();
  }, [completeGoogleLogin, navigate]);

  return (
    <main className="min-h-screen bg-black px-4 py-24 sm:px-6 lg:px-8 flex items-center justify-center">
      <div className="mx-auto max-w-md w-full rounded-3xl border border-white/10 bg-cynos-card p-8 text-center shadow-2xl shadow-black/20">
        {error ? (
          <>
            <div className="inline-flex items-center justify-center rounded-full bg-red-500/10 p-4 mb-4">
              <ShieldAlert className="h-6 w-6 text-cynos-red" />
            </div>
            <h1 className="text-2xl font-anton uppercase text-white">Sign-in failed</h1>
            <p className="mt-3 text-sm text-zinc-400">{error}</p>
            <button
              type="button"
              onClick={() => navigate("/signin", { replace: true })}
              className="mt-6 w-full rounded-2xl bg-cynos-red px-4 py-3 text-sm uppercase tracking-[0.2em] text-black hover:bg-cynos-red/90"
            >
              Back to Sign In
            </button>
          </>
        ) : (
          <>
            <div className="inline-flex items-center justify-center rounded-full bg-cynos-red/10 p-4 mb-4">
              <Loader2 className="h-6 w-6 text-cynos-red animate-spin" />
            </div>
            <h1 className="text-2xl font-anton uppercase text-white">Signing you in</h1>
            <p className="mt-3 text-sm text-zinc-400">Finishing your Google sign-in.</p>
          </>
        )}
      </div>
    </main>
  );
}
